/**
 * Settings Page - User settings and API key management
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';
import apiClient from '../api/client';
import toast from 'react-hot-toast';
import { format } from '../utils/date';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MobileMenu from '../components/MobileMenu';
import NeoSelect from '../components/NeoSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import { TierBadge } from '../components/TierBadge';
import { QuotaProgressBar } from '../components/QuotaProgressBar';
import { TierExpirationCountdown } from '../components/TierExpirationCountdown';
import { RedeemCodeModal } from '../components/RedeemCodeModal';
import { useUserInfo } from '../hooks/useUserInfo';
import { ApiDocumentation } from '../components/ApiDocumentation';

interface ApiKey {
  id: number;
  name: string;
  key_preview: string;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
  is_active: boolean;
  permissions: string[];
}

interface UserSettings {
  default_mailbox_duration: number;
  timezone: string;
  notifications_enabled: boolean;
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'profile' | 'api' | 'api-docs' | 'preferences'>('profile');
  const [showCreateApiKeyModal, setShowCreateApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [apiKeyForm, setApiKeyForm] = useState({
    name: '',
    expires_in: 0, // 0 means never expires
    permissions: ['read', 'write'],
  });
  const [showDeleteApiKeyDialog, setShowDeleteApiKeyDialog] = useState(false);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<number | null>(null);
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);

  // Fetch user info with tier and quota
  const { data: userInfo, isLoading: userInfoLoading } = useUserInfo();

  // Fetch API keys
  const { data: apiKeys, isLoading: apiKeysLoading } = useQuery<ApiKey[]>({
    queryKey: ['apiKeys'],
    queryFn: async () => {
      const response = await apiClient.get('/api/apikey/list');
      return response.data; // Fixed: response interceptor already unwrapped response.data
    },
    enabled: activeTab === 'api',
    staleTime: 5 * 60 * 1000, // 5 minutes - consider data fresh for 5 min
    refetchOnMount: false, // Don't refetch on component mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Fetch user settings
  const { data: settings } = useQuery<UserSettings>({
    queryKey: ['userSettings'],
    queryFn: async () => {
      const response = await apiClient.get('/api/user/settings');
      return response.data;
    },
    enabled: activeTab === 'preferences',
    staleTime: 5 * 60 * 1000, // 5 minutes - consider data fresh for 5 min
    refetchOnMount: false, // Don't refetch on component mount
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Create API key
  const createApiKey = useMutation({
    mutationFn: async (data: typeof apiKeyForm) => {
      return apiClient.post('/api/apikey/generate', data);
    },
    onSuccess: (response) => {
      setNewApiKey(response.data.key); // Fixed: response interceptor already unwrapped response.data
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      toast.success(t('settings.apiKeyCreated'));
    },
  });

  // Delete API key
  const deleteApiKey = useMutation({
    mutationFn: async (id: number) => {
      return apiClient.delete(`/api/apikey/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      toast.success(t('settings.apiKeyDeleted'));
    },
  });

  // Handle delete API key with confirmation
  const handleDeleteApiKey = (id: number) => {
    setApiKeyToDelete(id);
    setShowDeleteApiKeyDialog(true);
  };

  // Confirm delete API key action
  const confirmDeleteApiKey = () => {
    if (apiKeyToDelete !== null) {
      deleteApiKey.mutate(apiKeyToDelete);
    }
    setShowDeleteApiKeyDialog(false);
    setApiKeyToDelete(null);
  };

  // Update settings
  const updateSettings = useMutation({
    mutationFn: async (data: Partial<UserSettings>) => {
      return apiClient.patch('/api/user/settings', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      toast.success(t('settings.settingsUpdated'));
    },
  });

  // Copy API key to clipboard
  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success(t('settings.apiKeyCopied'));
  };

  return (
    <div className="min-h-screen bg-neo-warm-white bg-grain">
      {/* Header */}
      <header className="bg-white border-b-3 border-neo-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-bold text-neo-black">{t('settings.title')}</h1>
            <div className="hidden md:flex items-center gap-3">
              <LanguageSwitcher />
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-1.5 text-sm font-bold text-neo-black hover:bg-gray-50 rounded-neo-lg border-3 border-neo-black active:translate-x-0.5 active:translate-y-0.5 transition-all"
              >
                {t('settings.backToDashboard')}
              </button>
            </div>
            <MobileMenu>
              <div className="px-3 py-2"><LanguageSwitcher /></div>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full text-left px-3 py-2 text-sm font-bold text-neo-black hover:bg-gray-50 rounded-neo border-2 border-neo-black"
              >
                {t('settings.backToDashboard')}
              </button>
            </MobileMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-white border-3 border-neo-black rounded-neo-xl">
          {/* Tabs */}
          <div className="border-b-3 border-neo-black">
            <nav className="flex overflow-x-auto scrollbar-thin">
              <button
                onClick={() => setActiveTab('profile')}
                className={`relative px-4 sm:px-6 py-3 text-sm font-bold rounded-t-neo transition-all whitespace-nowrap ${
                  activeTab === 'profile'
                    ? 'bg-neo-yellow text-neo-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-neo-black'
                    : 'text-neo-gray hover:bg-gray-50'
                }`}
              >
                {t('settings.profile')}
              </button>
              <button
                onClick={() => setActiveTab('api')}
                className={`relative px-4 sm:px-6 py-3 text-sm font-bold rounded-t-neo transition-all whitespace-nowrap ${
                  activeTab === 'api'
                    ? 'bg-neo-yellow text-neo-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-neo-black'
                    : 'text-neo-gray hover:bg-gray-50'
                }`}
              >
                {t('settings.apiKeys')}
              </button>
              <button
                onClick={() => setActiveTab('api-docs')}
                className={`relative px-4 sm:px-6 py-3 text-sm font-bold rounded-t-neo transition-all whitespace-nowrap ${
                  activeTab === 'api-docs'
                    ? 'bg-neo-yellow text-neo-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-neo-black'
                    : 'text-neo-gray hover:bg-gray-50'
                }`}
              >
                {t('settings.apiDocs')}
              </button>
              <button
                onClick={() => setActiveTab('preferences')}
                className={`relative px-4 sm:px-6 py-3 text-sm font-bold rounded-t-neo transition-all whitespace-nowrap ${
                  activeTab === 'preferences'
                    ? 'bg-neo-yellow text-neo-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-neo-black'
                    : 'text-neo-gray hover:bg-gray-50'
                }`}
              >
                {t('settings.preferences')}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-neo-black mb-4">{t('settings.profileInformation')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.username')}</label>
                      <input
                        type="text"
                        value={user?.username || ''}
                        disabled
                        className="input-neo w-full disabled:bg-gray-100 disabled:text-neo-gray"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.email')}</label>
                      <input
                        type="email"
                        value={user?.email || ''}
                        disabled
                        className="input-neo w-full disabled:bg-gray-100 disabled:text-neo-gray"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.accountCreated')}</label>
                      <input
                        type="text"
                        value={user?.created_at ? format(new Date(user.created_at), 'PPP') : ''}
                        disabled
                        className="input-neo w-full disabled:bg-gray-100 disabled:text-neo-gray"
                      />
                    </div>
                  </div>
                </div>

                {/* Tier Information Section */}
                <div className="pt-6 border-t-3 border-neo-black">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-neo-black">{t('settings.tier_info')}</h3>
                    <button
                      onClick={() => setIsRedeemModalOpen(true)}
                      className="btn-neo-secondary text-sm"
                    >
                      {t('settings.redeem_code_button')}
                    </button>
                  </div>

                  {userInfoLoading ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border-2 border-neo-black rounded-lg bg-gray-50">
                        <div className="skeleton-neo h-4 w-24"></div>
                        <div className="skeleton-neo h-6 w-20 rounded-full"></div>
                      </div>
                      <div className="flex items-center justify-between p-4 border-2 border-neo-black rounded-lg bg-gray-50">
                        <div className="skeleton-neo h-4 w-24"></div>
                        <div className="skeleton-neo h-4 w-32"></div>
                      </div>
                      <div className="p-4 border-2 border-neo-black rounded-lg bg-gray-50">
                        <div className="skeleton-neo h-4 w-20 mb-4"></div>
                        <div className="space-y-4">
                          <div className="skeleton-neo h-2 w-full rounded-full"></div>
                          <div className="skeleton-neo h-2 w-full rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  ) : userInfo?.data ? (
                    <div className="space-y-4">
                      {/* Current Tier */}
                      <div className="flex items-center justify-between p-4 border-2 border-neo-black rounded-lg bg-gray-50">
                        <span className="font-medium text-neo-black">{t('settings.current_tier')}</span>
                        <TierBadge
                          tierName={userInfo.data.tier.name}
                          displayName={userInfo.data.tier.display_name}
                          size="lg"
                        />
                      </div>

                      {/* Expiration Time */}
                      <div className="flex items-center justify-between p-4 border-2 border-neo-black rounded-lg bg-gray-50">
                        <span className="font-medium text-neo-black">{t('settings.expiration_time')}</span>
                        <TierExpirationCountdown
                          expiresAt={userInfo.data.tier.expires_at}
                          format="full"
                        />
                      </div>

                      {/* Quota Usage */}
                      <div className="p-4 border-2 border-neo-black rounded-lg bg-gray-50">
                        <h4 className="font-bold text-neo-black mb-4">{t('settings.quota_usage')}</h4>
                        <div className="space-y-4">
                          <QuotaProgressBar
                            type="permanent"
                            current={userInfo.data.quota.permanent.used}
                            max={userInfo.data.quota.permanent.limit}
                            showPercentage={true}
                          />
                          <QuotaProgressBar
                            type="temporary"
                            current={userInfo.data.quota.temporary.used}
                            max={userInfo.data.quota.temporary.limit}
                            showPercentage={true}
                          />
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>

                {/* Password Change Section - Only for password-based accounts */}
                {!user?.oauth_provider && (
                  <div className="pt-6 border-t-3 border-neo-black">
                    <h3 className="text-lg font-bold text-neo-black mb-4">{t('settings.changePassword')}</h3>
                    <form className="space-y-4">
                      <div>
                        <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.currentPassword')}</label>
                        <input
                          type="password"
                          className="input-neo w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.newPassword')}</label>
                        <input
                          type="password"
                          className="input-neo w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.confirmPassword')}</label>
                        <input
                          type="password"
                          className="input-neo w-full"
                        />
                      </div>
                      <button
                        type="submit"
                        className="btn-neo-primary"
                      >
                        {t('settings.updatePassword')}
                      </button>
                    </form>
                  </div>
                )}

                {/* OAuth Account Notice */}
                {user?.oauth_provider && (
                  <div className="pt-6 border-t-3 border-neo-black">
                    <div className="bg-blue-50 border-3 border-blue-500 rounded-lg p-6">
                      <div className="flex items-start">
                        <svg className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <h4 className="text-lg font-bold text-blue-900 mb-2">OAuth Account</h4>
                          <p className="text-sm text-blue-800">
                            You are logged in using {user.oauth_provider === 'linuxdo' ? 'Linux.do' : user.oauth_provider}.
                            Password management is not available for OAuth accounts. To log in, simply click "Login with {user.oauth_provider === 'linuxdo' ? 'Linux.do' : user.oauth_provider}" on the login page.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* API Keys Tab */}
            {activeTab === 'api' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-neo-black">{t('settings.apiKeys')}</h3>
                    <p className="text-sm text-neo-gray mt-1">
                      {t('settings.apiKeysDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCreateApiKeyModal(true)}
                    className="btn-neo-secondary text-sm"
                  >
                    + {t('settings.createNewKey')}
                  </button>
                </div>

                {apiKeysLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-neo-spin rounded-full h-8 w-8 border-4 border-neo-black border-t-neo-cyan"></div>
                  </div>
                ) : apiKeys?.length === 0 ? (
                  <div className="text-center py-12 border-3 border-neo-black rounded-neo-xl">
                    <svg
                      className="mx-auto h-12 w-12 text-neo-gray"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                      />
                    </svg>
                    <h3 className="mt-4 text-lg font-bold text-neo-black">{t('settings.noApiKeys')}</h3>
                    <p className="mt-2 text-neo-gray">{t('settings.createFirstKey')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {apiKeys?.map((key) => (
                      <div key={key.id} className="border-3 border-neo-black rounded-neo-xl p-4 bg-white">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h4 className="font-bold text-neo-black">{key.name}</h4>
                              {key.is_active ? (
                                <span className="ml-2 badge-neo bg-neo-green text-neo-black text-xs">
                                  {t('settings.active')}
                                </span>
                              ) : (
                                <span className="ml-2 badge-neo bg-gray-200 text-neo-gray text-xs">
                                  {t('settings.inactive')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-neo-gray mt-1 font-mono">{key.key_preview}</p>
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-neo-gray mt-2">
                              <span>{t('settings.created')}: {format(new Date(key.created_at), 'PP')}</span>
                              {key.last_used_at && (
                                <span>{t('settings.lastUsed')}: {format(new Date(key.last_used_at), 'PP')}</span>
                              )}
                              {key.expires_at && (
                                <span>{t('settings.expires')}: {format(new Date(key.expires_at), 'PP')}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              {key.permissions.map((perm) => (
                                <span
                                  key={perm}
                                  className="badge-neo bg-neo-yellow text-neo-black text-xs"
                                >
                                  {perm}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteApiKey(key.id)}
                            disabled={deleteApiKey.isPending}
                            className="ml-4 btn-neo-danger text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {t('settings.delete')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-neo-black mb-4">{t('settings.emailPreferences')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-neo-black mb-1">
                        {t('settings.defaultDuration')}
                      </label>
                      <NeoSelect
                        value={settings?.default_mailbox_duration || 3600}
                        onChange={(value) =>
                          updateSettings.mutate({ default_mailbox_duration: Number(value) })
                        }
                        options={[
                          { value: 600, label: t('settings.10minutes') },
                          { value: 1800, label: t('settings.30minutes') },
                          { value: 3600, label: t('settings.1hour') },
                          { value: 7200, label: t('settings.2hours') },
                          { value: 21600, label: t('settings.6hours') },
                          { value: 43200, label: t('settings.12hours') },
                          { value: 86400, label: t('settings.24hours') },
                          { value: 0, label: t('settings.neverExpires') },
                        ]}
                        className="w-full"
                      />
                    </div>

                    </div>
                </div>

                <div className="pt-6 border-t-3 border-neo-black">
                  <h3 className="text-lg font-bold text-neo-black mb-4">{t('settings.notifications')}</h3>
                  <div className="space-y-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings?.notifications_enabled || false}
                        onChange={(e) =>
                          updateSettings.mutate({ notifications_enabled: e.target.checked })
                        }
                        className="w-5 h-5 border-3 border-neo-black rounded-neo-xs accent-neo-cyan hover:border-4 transition-all"
                      />
                      <span className="ml-3 text-sm font-bold text-neo-black">
                        {t('settings.enableNotifications')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="pt-6 border-t-3 border-neo-black">
                  <h3 className="text-lg font-bold text-neo-black mb-4">{t('settings.timezone')}</h3>
                  <NeoSelect
                    value={settings?.timezone || 'UTC'}
                    onChange={(value) => updateSettings.mutate({ timezone: String(value) })}
                    options={[
                      { value: 'UTC', label: t('settings.timezones.utc') },
                      { value: 'America/New_York', label: t('settings.timezones.eastern') },
                      { value: 'America/Chicago', label: t('settings.timezones.central') },
                      { value: 'America/Denver', label: t('settings.timezones.mountain') },
                      { value: 'America/Los_Angeles', label: t('settings.timezones.pacific') },
                      { value: 'Europe/London', label: t('settings.timezones.london') },
                      { value: 'Europe/Paris', label: t('settings.timezones.paris') },
                      { value: 'Asia/Tokyo', label: t('settings.timezones.tokyo') },
                      { value: 'Asia/Shanghai', label: t('settings.timezones.shanghai') },
                    ]}
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {/* API Documentation Tab */}
            {activeTab === 'api-docs' && (
              <div className="max-w-5xl mx-auto">
                {/* AI-Friendly API Docs Entry Card */}
                <div className="card-neo mb-8 bg-neo-yellow border-neo-black">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-neo-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold mb-1">AI-Friendly API 文档</h3>
                        <p className="text-gray-700 text-sm">提供 JSON 格式的工具描述和可直接复制的代码示例，专为 AI 代理设计</p>
                      </div>
                    </div>
                    <a
                      href="/developers"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-neo-primary whitespace-nowrap"
                    >
                      访问完整文档
                    </a>
                  </div>
                </div>
                <ApiDocumentation />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Create API Key Modal */}
      {showCreateApiKeyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-3 border-neo-black rounded-neo-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-neo-black mb-4">
              {newApiKey ? t('settings.apiKeyCreated') : t('settings.createNewApiKey')}
            </h3>

            {newApiKey ? (
              <div>
                <div className="p-4 bg-neo-yellow border-3 border-neo-black rounded-neo-lg mb-4">
                  <p className="text-sm font-bold text-neo-black mb-2">
                    ⚠️ {t('settings.copyKeyWarning')}
                  </p>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newApiKey}
                      readOnly
                      className="input-neo flex-1 font-mono text-sm"
                    />
                    <button
                      onClick={() => copyApiKey(newApiKey)}
                      className="btn-neo-secondary text-sm"
                    >
                      {t('settings.copy')}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowCreateApiKeyModal(false);
                    setNewApiKey(null);
                    setApiKeyForm({
                      name: '',
                      expires_in: 0,
                      permissions: ['read', 'write'],
                    });
                  }}
                  className="btn-neo-primary w-full"
                >
                  {t('settings.done')}
                </button>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createApiKey.mutate(apiKeyForm);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.keyName')}</label>
                  <input
                    type="text"
                    value={apiKeyForm.name}
                    onChange={(e) => setApiKeyForm({ ...apiKeyForm, name: e.target.value })}
                    required
                    placeholder={t('settings.keyNamePlaceholder')}
                    className="input-neo w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.expiresIn')}</label>
                  <NeoSelect
                    value={apiKeyForm.expires_in}
                    onChange={(value) =>
                      setApiKeyForm({ ...apiKeyForm, expires_in: Number(value) })
                    }
                    options={[
                      { value: 0, label: t('settings.never') },
                      { value: 2592000, label: t('settings.30days') },
                      { value: 7776000, label: t('settings.90days') },
                      { value: 31536000, label: t('settings.1year') },
                    ]}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-neo-black mb-1">{t('settings.permissions')}</label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={apiKeyForm.permissions.includes('read')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setApiKeyForm({
                              ...apiKeyForm,
                              permissions: [...apiKeyForm.permissions, 'read'],
                            });
                          } else {
                            setApiKeyForm({
                              ...apiKeyForm,
                              permissions: apiKeyForm.permissions.filter((p) => p !== 'read'),
                            });
                          }
                        }}
                        className="w-5 h-5 border-3 border-neo-black rounded-neo-xs accent-neo-cyan hover:border-4 transition-all"
                      />
                      <span className="ml-2 text-sm font-bold text-neo-black">{t('settings.read')}</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={apiKeyForm.permissions.includes('write')}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setApiKeyForm({
                              ...apiKeyForm,
                              permissions: [...apiKeyForm.permissions, 'write'],
                            });
                          } else {
                            setApiKeyForm({
                              ...apiKeyForm,
                              permissions: apiKeyForm.permissions.filter((p) => p !== 'write'),
                            });
                          }
                        }}
                        className="w-5 h-5 border-3 border-neo-black rounded-neo-xs accent-neo-cyan hover:border-4 transition-all"
                      />
                      <span className="ml-2 text-sm font-bold text-neo-black">{t('settings.write')}</span>
                    </label>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowCreateApiKeyModal(false)}
                    className="btn-neo-ghost flex-1"
                  >
                    {t('settings.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={!apiKeyForm.name || createApiKey.isPending}
                    className="btn-neo-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {createApiKey.isPending ? t('settings.creating') : t('settings.createKey')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Redeem Code Modal */}
      <RedeemCodeModal
        isOpen={isRedeemModalOpen}
        onClose={() => setIsRedeemModalOpen(false)}
      />

      {/* Delete API Key Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteApiKeyDialog}
        title={t('common.confirm')}
        message={t('settings.confirmDeleteKey')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDeleteApiKey}
        onCancel={() => {
          setShowDeleteApiKeyDialog(false);
          setApiKeyToDelete(null);
        }}
        isLoading={deleteApiKey.isPending}
      />
    </div>
  );
}